import { DepartmentLabel, useCurrentDepartmentText, useDepartmentText } from 'model/DepartmentTerminology';
import { Group, LoadingOverlay, Menu, Portal, Text, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBackspace, IconDotsVertical, IconPencil, IconPlus, IconTrashX, IconServer } from '@tabler/icons-react';
import { useApi } from 'api/api-context';
import { useAcknowledge, useConfirm } from 'components/modals/message/MessageProvider';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import panic from 'errors/panic';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ADD_DEPARTMENT_PAGE_PATH,
  ADD_DEVICE_PAGE_PATH,
  DEVICES_PAGE_PATH,
  EDIT_DEPARTMENT_PAGE_PATH,
} from 'routes/paths';
import { CANNOT_SET_ENTITY_STATUS, ERROR_NOTIFICATION_COLOR } from 'utils/constants';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import { DepartmentFormMode } from 'components/tables/data/department/form-presentation';

/**
 * Parameters of the DepartmentActions component.
 */
export interface DepartmentActionsProps {
  departmentId: number;
  departmentName: string;
  canDiscard: boolean;
  status: boolean;
  compactOnTablet?: boolean;
  isLeafLevel?: boolean;
  onStatusChange: (status: boolean) => void;
  organization?: { organizationId: number };
  presentationMode?: DepartmentFormMode;
  onOpenEdit?: (departmentId: number, departmentName: string) => void;
  onOpenChild?: (departmentId: number, organizationId: number) => void;
}

/**
 * Displays actions for a department in a table.
 */
export default function DepartmentActions({
  departmentId,
  departmentName,
  canDiscard,
  status,
  compactOnTablet = false,
  isLeafLevel = false,
  onStatusChange,
  organization,
  presentationMode = 'same-tab',
  onOpenEdit,
  onOpenChild,
}: DepartmentActionsProps) {
  const departmentText = useDepartmentText();
  const currentDepartmentText = useCurrentDepartmentText();
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const { acknowledge } = useAcknowledge();
  const { isMobile, tableActionMode } = useResponsiveDesign();
  const isCompact = compactOnTablet && tableActionMode !== 'desktop';
  const menuOnly = compactOnTablet && tableActionMode === 'mobile';
  const [loading, setLoading] = useState<boolean>(true);
  const [opened, setOpened] = useState<boolean>(false);
  const [canManageDevices, setCanManageDevices] = useState<boolean>(false);
  const overlayMode = presentationMode === 'drawer' || presentationMode === 'modal';
  const linkTarget = presentationMode === 'new-tab' ? '_blank' : undefined;
  const editPath = EDIT_DEPARTMENT_PAGE_PATH.insert({ departmentId });
  const childPath = `${ADD_DEPARTMENT_PAGE_PATH.original}?organizationId=${
    organization?.organizationId
  }&parentDepartmentId=${departmentId}${linkTarget ? '&presentation=new-tab' : ''}`;
  /** Opens a list overlay while retaining the route as the link fallback. */
  const onEditClick = (event: MouseEvent) => {
    if (overlayMode) {
      event.preventDefault();
      onOpenEdit?.(departmentId, departmentName);
    }
  };
  /** Opens child creation with its organization and parent preselected. */
  const onChildClick = (event: MouseEvent) => {
    if (overlayMode) {
      event.preventDefault();
      onOpenChild?.(departmentId, organization!.organizationId);
    }
  };

  /**
   * Makes a call to the API to set the status of the department.
   */
  const setStatusRemote = useCallback(
    (status: boolean) => {
      const departmentSetStatus = getAction('DepartmentSetStatus');

      departmentSetStatus({ parameters: { departmentId: String(departmentId) }, payload: { status } })
        .then(() => onStatusChange(status))
        .catch((e) => {
          if (e.response.data.error.code === CANNOT_SET_ENTITY_STATUS) {
            notifications.show({
              title: 'Chyba!',
              message: currentDepartmentText(
                'Túto položku úrovne nemôžete deaktivovať.',
                'Toto stredisko nemôžete deaktivovať.'
              ),
              color: ERROR_NOTIFICATION_COLOR,
            });
          } else {
            panic(e);
          }
        });
    },
    [currentDepartmentText, getAction, departmentId]
  );

  /**
   * Confirms the status change.
   */
  const confirmSetStatus = useCallback(
    (status: boolean) => {
      if (!status && !canDiscard) {
        acknowledge({
          title: (
            <DepartmentLabel objectLabel="Nemožno vyradiť položku úrovne" departmentLabel="Nemožno vyradiť stredisko" />
          ),
          content: (
            <DepartmentLabel
              objectLabel="Podstrom položky úrovne obsahuje aktívnu položku úrovne, zariadenie alebo osobu. Najskôr odstráňte všetky blokujúce väzby."
              departmentLabel="Podstrom strediska obsahuje aktívne stredisko, zariadenie alebo osobu. Najskôr odstráňte všetky blokujúce väzby."
            />
          ),
        });
      } else {
        confirm({
          title: (
            <DepartmentLabel
              objectLabel={status ? 'Zaradiť položku úrovne' : 'Vyradiť položku úrovne'}
              departmentLabel={status ? 'Zaradiť stredisko' : 'Vyradiť stredisko'}
            />
          ),
          content: (
            <DepartmentLabel
              objectLabel={status ? 'Naozaj chcete zaradiť položku úrovne?' : 'Naozaj chcete vyradiť položku úrovne?'}
              departmentLabel={status ? 'Naozaj chcete zaradiť stredisko?' : 'Naozaj chcete vyradiť stredisko?'}
            />
          ),
          onConfirm: () => setStatusRemote(status),
        });
      }
    },
    [confirm, setStatusRemote]
  );

  // Check permissions on open.
  useEffect(() => {
    if (opened) {
      const action = getAction('AuthUserHasPermissionInDepartment');

      action({ parameters: { departmentId: String(departmentId), permissionSlug: 'manage-devices' } })
        .then(({ hasPermission }) => setCanManageDevices(hasPermission))
        .catch(panic)
        .finally(() => setLoading(false));
    }
  }, [opened]);

  return (
    <Group spacing={12} noWrap>
      {!menuOnly &&
        (isCompact ? (
          <Tooltip label="Editovať" events={{ hover: true, focus: true, touch: true }}>
            <ActionIcon
              variant="secondary"
              size="md"
              component={Link}
              to={editPath}
              target={linkTarget}
              rel={linkTarget ? 'noopener noreferrer' : undefined}
              onClick={onEditClick}
              disabled={!status}
            >
              <IconPencil />
            </ActionIcon>
          </Tooltip>
        ) : (
          <Button
            size="md"
            component={Link}
            to={editPath}
            target={linkTarget}
            rel={linkTarget ? 'noopener noreferrer' : undefined}
            onClick={onEditClick}
            variant="secondary"
            leftIcon={<IconPencil />}
            disabled={!status}
          >
            Editovať
          </Button>
        ))}
      <Menu position="bottom-end" opened={opened} onChange={setOpened}>
        <Menu.Target>
          <ActionIcon variant="tertiary" size={isMobile ? 'sm' : 'md'}>
            <IconDotsVertical stroke="1.5" />
          </ActionIcon>
        </Menu.Target>
        <Portal>
          <Menu.Dropdown>
            <LoadingOverlay visible={loading} loaderProps={{ size: 12 }} />
            <Menu.Label>
              <Text maw={160} truncate>
                {departmentName}
              </Text>
            </Menu.Label>
            {menuOnly && status && (
              <Menu.Item
                component={Link}
                to={editPath}
                target={linkTarget}
                rel={linkTarget ? 'noopener noreferrer' : undefined}
                onClick={onEditClick}
                icon={<IconPencil stroke={1.5} size={20} />}
              >
                Editovať
              </Menu.Item>
            )}
            {status && canManageDevices && (
              <>
                <Menu.Item
                  component={Link}
                  to={`${ADD_DEVICE_PAGE_PATH.original}?departmentId=${departmentId}`}
                  icon={<IconPlus stroke={1.5} size={20} />}
                  color="gray.8"
                >
                  Pridať zariadenie
                </Menu.Item>
                <Menu.Item
                  component={Link}
                  to={`${DEVICES_PAGE_PATH.original}?departmentId=${departmentId}`}
                  icon={<IconServer stroke="1.5" size={20} />}
                  color="gray.8"
                >
                  Zoraziť zariadenia
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            {status && organization && !isLeafLevel && (
              <>
                <Menu.Item
                  component={Link}
                  to={childPath}
                  target={linkTarget}
                  rel={linkTarget ? 'noopener noreferrer' : undefined}
                  onClick={onChildClick}
                  icon={<IconPlus stroke={1.5} size={20} />}
                  color="gray.8"
                >
                  {departmentText('Pridať podriadenú položku úrovne', 'Pridať podriadené stredisko')}
                </Menu.Item>
                <Menu.Divider />
              </>
            )}
            <Menu.Item
              color={status ? 'red.8' : 'red.8'}
              onClick={() => confirmSetStatus(!status)}
              icon={status ? <IconTrashX stroke={1.5} size={20} /> : <IconBackspace stroke={1.5} size={20} />}
            >
              {status ? 'Vyradiť' : 'Zaradiť'}
            </Menu.Item>
          </Menu.Dropdown>
        </Portal>
      </Menu>
    </Group>
  );
}
